// Customer Add index
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { Row, Col, Input, Button, Form, Drawer, Radio, Select, Divider, InputNumber } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { putApi } from 'redux/sagas/putApiSaga';
import { API_STATUS, DATE_FORMAT } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { formQueryStringFromObject } from 'helpers';
import AddCoach from 'pages/coaches/add-coach';
import FileUpload from 'components/file-upload/file-upload';
import DatePicker from 'components/date-picker';
import { sendGetRequest } from 'redux/sagas/utils';
import ServiceModal from 'components/services-modal';
import MembershipModal from 'components/memberships-modal';
import { useI18n } from 'hooks/useI18n';

const AddCustomer = ({ customerAddModal, width = '40%', editCustomer, setCustomerAddModal, refreshList, handleClose, isVisitor = false }) => {
	const [form] = Form.useForm();
	const [coachAddModal, setCoachAddModal] = useState(false);
	const [customerImage, setCustomerImage] = useState([]);
	const [allCustomers, setAllCustomers] = useState({
		data: [],
		loading: true,
	});
	const memberships = useSelector((state) => state.membershipRedux?.memberships || []);
	// const services = useSelector((state) => state?.serviceRedux?.services || []);
	const coaches = useSelector((state) => state.coachRedux?.coaches);
	const globalRedux = useSelector((state) => state.globalRedux);
	const branchId = globalRedux?.selectedBranch?.id;
	const serviceIdValue = Form.useWatch('serviceId', form);
	const joiningDateValue = Form.useWatch('joiningDate', form);
	const dobValue = Form.useWatch('dob', form);
	const membershipIdValue = Form.useWatch('membershipId', form);
	const dispatch = useDispatch();
	const { t } = useI18n();
	const GENDER_OPTIONS = [
		{ label: t('customers.male'), value: 'male' },
		{ label: t('customers.female'), value: 'female' },
	];
	const KNOWN_BY = [
		{ label: t('customers.known_by_options.facebook'), value: 'Facebook' },
		{ label: t('customers.known_by_options.instagram'), value: 'Instagram' },
		{ label: t('customers.known_by_options.google'), value: 'Google' },
		{ label: t('customers.known_by_options.friend'), value: 'Friend' },
		{ label: t('customers.known_by_options.walk_in'), value: 'WalkIn' },
		{ label: t('customers.known_by_options.youtube'), value: 'Youtube' },
	];
	const PAYMENT_METHOD = [
		{ label: t('payments.payment_methods.cash'), value: 'cash' },
		{ label: t('payments.payment_methods.upi'), value: 'upi' },
	];
	const paymentMethodValue = Form.useWatch('paymentMethod', form);
	const amountValue = Form.useWatch('amount', form);
	const paidValue = Form.useWatch('paid', form);

	useEffect(() => {
		if (dobValue) {
			let age = moment().diff(dobValue, 'years');
			form.setFieldsValue({
				age,
			});
		}
	}, [dobValue, form]);

	const string = useMemo(() => {
		const string = formQueryStringFromObject({
			branchId,
		});
		return string;
	}, [branchId]);

	const getMemberships = useCallback(
		(serviceId) => {
			const query = formQueryStringFromObject({
				serviceId,
			});
			let membershipUrl = `${SERVER_IP}api/memberships?${string}&${query}`;
			dispatch(getApi('GET_MEMBERSHIPS', membershipUrl));
		},
		[dispatch, string]
	);

	useEffect(() => {
		let balance = amountValue - (paidValue || 0);
		form.setFieldsValue({
			balance: customerAddModal ? (balance || 0) : 0,
		});
	}, [amountValue, form, customerAddModal, paidValue]);

	useEffect(() => {
		if (membershipIdValue) {
			const membership = memberships?.find((membership) => membership?.id === membershipIdValue);
			const amount = membership?.offerPrice || membership?.originalPrice;
			form.setFieldsValue({
				amount,
				paidOn: joiningDateValue || moment(),
				nextBillDate: moment(joiningDateValue).add(membership?.days, 'days'),
			});
		}
	}, [membershipIdValue, form, memberships, joiningDateValue]);

	const getCustomers = useCallback(async () => {
		setAllCustomers((allCustomers) => ({
			...allCustomers,
			loading: true,
		}));
		const { data } = await sendGetRequest(null, `${SERVER_IP}api/customers/getCustomerSB?${string}`);
		console.log('🚀 ~ getCustomers ~ data:', data?.data);
		setAllCustomers({
			data: data?.data || [],
			loading: false,
		});
	}, [string]);

	const getCoaches = useCallback(() => {
		const string = formQueryStringFromObject({
			branchId,
		});
		let url = `${SERVER_IP}api/coaches?${string}`;
		customerAddModal && dispatch(getApi('GET_COACHES', url));
	}, [dispatch, branchId, customerAddModal]);

	useEffect(() => {
		if (serviceIdValue) {
			form.setFieldsValue({
				membershipId: '',
				amount: '',
			});
			getMemberships(serviceIdValue);
		}
	}, [serviceIdValue, getMemberships, form]);

	useEffect(() => {
		if (customerAddModal) {
			getCustomers();
			getCoaches();
		}
	}, [getCoaches, getCustomers, customerAddModal]);

	useEffect(() => {
		setCustomerImage([]);
		const customerImage = JSON.parse(JSON.stringify(editCustomer));
		if (!isVisitor && customerImage) {
			if (customerImage.image && customerImage.image.length && customerImage?.image[0]?.url) {
				customerImage.image[0] = typeof customerImage?.image[0] === 'string' ? JSON.parse(customerImage?.image[0]) : customerImage?.image[0];
				customerImage.image[0].url = String(customerImage?.image[0]?.url).includes(SERVER_IP)
					? customerImage?.image[0]?.url
					: SERVER_IP + customerImage?.image[0]?.url;
			} else {
				customerImage.image = [];
			}

			form.setFieldsValue({
				customerName: editCustomer?.customerName || '',
				displayName: editCustomer?.displayName || '',
				age: editCustomer?.age || '',
				dob: editCustomer?.dob ? moment(editCustomer?.dob) : moment().subtract(18, 'years'),
				nextBillDate: editCustomer?.nextBillDate ? moment(editCustomer?.nextBillDate) : moment(),
				email: editCustomer?.email || '',
				mobile: editCustomer?.mobile || '',
				weight: editCustomer?.weight || '',
				height: editCustomer?.height || '',
				occupation: editCustomer?.occupation || '',
				address: editCustomer?.address || '',
				gender: editCustomer?.gender || '',
				joiningDate: moment(editCustomer?.joiningDate) || '',
				referredBy: editCustomer?.referredBy || '',
				knownBy: editCustomer?.knownBy || '',
				onBoardedBy: editCustomer?.onBoardedBy || '',
				aadhaarNo: editCustomer?.aadhaarNo || '',
				visitorId: editCustomer?.visitorId || '',
				image: customerImage.image || [],
			});
			if (customerImage?.image?.length) {
				setCustomerImage(customerImage?.image);
			}
		} else {
			form?.resetFields();
			if (isVisitor) {
				form.setFieldsValue({
					customerName: editCustomer?.customerName || '',
					displayName: editCustomer?.displayName || '',
					mobile: editCustomer?.mobile || '',
					visitorId: editCustomer?.visitorId || '',
				});
			}
		}
	}, [editCustomer, form, isVisitor]);

	const handleSubmit = (values) => {
		const customerPic = {
			uid: customerImage[0]?.uid,
			url: customerImage[0]?.url?.replace(SERVER_IP, '') || '',
			name: customerImage[0]?.name,
			thumbUrl: customerImage[0]?.thumbUrl,
		};

		customerImage[0] = customerPic;

		let data = {
			customerName: values?.customerName || '',
			displayName: values?.displayName || '',
			age: values?.age || '',
			dob: values?.dob || '',
			weight: values?.weight || '',
			height: values?.height || '',
			occupation: values?.occupation || '',
			email: values?.email || '',
			mobile: values?.mobile || '',
			aadhaarNo: values?.aadhaarNo || '',
			address: values?.address || '',
			gender: values?.gender || '',
			joiningDate: values?.joiningDate || editCustomer?.joiningDate || '',
			referredBy: values?.referredBy || '',
			knownBy: values?.knownBy || editCustomer?.knownBy || '',
			onBoardedBy: values?.onBoardedBy || '',
			image: customerImage,
			branchId,

			...(!editCustomer && {
				serviceId: values?.serviceId,
				membershipId: values?.membershipId,
				amount: values?.amount,
				paid: values?.paid,
				balance: values?.balance,
				role: 'customer',
				paymentMethod: values?.paymentMethod || 'cash',
				paidOn: values?.paidOn || '',
				nextBillDate: values?.nextBillDate || '',
				remarks: values?.remarks || '',
				upiTransactionId: values?.upiTransactionId || '',
				image: customerImage,
				visitorId: values?.visitorId || null,
			}),
		};

		if (!editCustomer || isVisitor) {
			dispatch(postApi(data, 'ADD_CUSTOMER'));
		} else {
			let url = `${SERVER_IP}api/customers/${editCustomer.id}`;
			dispatch(putApi(data, 'EDIT_CUSTOMER', url));
		}
	};

	useEffect(() => {
		if (globalRedux.apiStatus.ADD_CUSTOMER === 'SUCCESS' || globalRedux.apiStatus.EDIT_CUSTOMER === 'SUCCESS') {
			handleClose?.();
			refreshList?.(true);
			dispatch(resetApiStatus(editCustomer ? 'EDIT_CUSTOMER' : 'ADD_CUSTOMER'));
			form?.resetFields();
		}
	}, [globalRedux.apiStatus, editCustomer, setCustomerAddModal, dispatch, refreshList, handleClose, form]);

	const layer1FormCol = {
		labelCol: {
			span: 12,
		},
		wrapperCol: {
			span: 12,
		},
	};

	const loading = globalRedux.apiStatus.ADD_CUSTOMER === API_STATUS.PENDING || globalRedux.apiStatus.EDIT_CUSTOMER === API_STATUS.PENDING;
	// const membershipLoading = useMemo(() => globalRedux?.apiStatus.GET_MEMBERSHIPS === API_STATUS.PENDING, [globalRedux?.apiStatus?.GET_MEMBERSHIPS]);
	// const serviceLoading = useMemo(() => globalRedux?.apiStatus.GET_SERVICES === API_STATUS.PENDING, [globalRedux?.apiStatus?.GET_SERVICES]);

	return (
		<>
			<Drawer
				placement="right"
				title={`${editCustomer ? t('customers.edit_customer') : t('customers.create_customer')}`}
				width={width || '40%'}
				open={customerAddModal}
				closable
				onClose={() => setCustomerAddModal(false)}
				destroyOnClose={true}>
				<Row>
					<Col span={24}>
						<Form
							name="add-customer"
							className="required_in_right"
							style={{ width: '100%' }}
							colon={true}
							labelAlign="left"
							form={form}
							onFinish={handleSubmit}
							initialValues={{}}
							{...layer1FormCol}>
							<Form.Item
								label={t('customers.customer_name')}
								name="customerName"
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}>
								<Input placeholder={t('customers.customer_name')} />
							</Form.Item>
							<Form.Item label={t('customers.display_name')} name="displayName" rules={[]}>
								<Input placeholder={t('customers.display_name')} />
							</Form.Item>
							<Form.Item
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}
								initialValue={moment().subtract(18, 'years')}
								label={t('customers.date_of_birth')}
								name="dob">
								<DatePicker style={{ width: '100%' }} format={DATE_FORMAT.DD_MM_YYYY} />
							</Form.Item>
							<Form.Item
								label={t('customers.age')}
								name="age"
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}>
								<Input placeholder={t('customers.age')} />
							</Form.Item>
							<Form.Item
								label={t('customers.gender')}
								name="gender"
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}>
								<Radio.Group options={GENDER_OPTIONS} optionType="button" buttonStyle="solid" />
							</Form.Item>
							<Form.Item
								label={t('customers.mobile')}
								name="mobile"
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}>
								<Input maxLength={10} placeholder={t('customers.mobile')} />
							</Form.Item>
							<Form.Item label={t('customers.email')} name="email">
								<Input placeholder={t('customers.email')} />
							</Form.Item>
							<Form.Item
								rules={[
									{
										required: false,
										message: t('forms.this_field_required'),
									},
								]}
								label={t('customers.aadhaar')}
								name="aadhaarNo">
								<Input placeholder={t('customers.aadhaar')} />
							</Form.Item>
							<Form.Item
								label={t('customers.address')}
								name="address"
								rules={[
									{
										required: false,
										message: t('forms.this_field_required'),
									},
								]}>
								<Input.TextArea placeholder={t('customers.address')} />
							</Form.Item>
							<Form.Item label={t('customers.known_by')} name="knownBy">
								<Select
									placeholder={t('customers.known_by')}
									options={KNOWN_BY}
								/>
							</Form.Item>
							<Form.Item label={t('customers.referred_by')} name="referredBy">
								<Select
									placeholder={t('customers.referred_by')}
									showSearch
									optionFilterProp="children"
									filterOption={(input, option) => {
										return option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0;
									}}>
									{(allCustomers?.data || [])?.map((customer) => (
										<Select.Option value={customer.id}>{`${customer?.customerName} - ${customer?.mobile}`}</Select.Option>
									))}
								</Select>
							</Form.Item>
							<Form.Item
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}
								label={t('customers.on_boarded_by')}
								name="onBoardedBy">
								<Select
									placeholder={t('customers.on_boarded_by')}
									dropdownRender={(menu) => (
										<>
											{menu}
											<Divider />
											<div style={{}}>
												<a
													href
													style={{ color: '#188dfa', padding: 10, cursor: 'pointer' }}
													onClick={() => setCoachAddModal(true)}>
													<PlusOutlined /> {t('coaches.title')}
												</a>
											</div>
										</>
									)}>
									{coaches?.map((coach) => (
										<Select.Option key={coach.id} value={coach.id}>
											{coach?.coachName}
										</Select.Option>
									))}
								</Select>
							</Form.Item>

							<Row>
								<hr />
								<Col span={24} style={{ fontWeight: 'bold' }}>
									{t('customers.personal_details')}
								</Col>
								<hr />
							</Row>
							<Form.Item
								label={t('customers.height')}
								name="height"
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}>
								<InputNumber placeholder={t('customers.height')} style={{ width: '100%' }} />
							</Form.Item>
							<Form.Item
								label={t('customers.weight')}
								name="weight"
								rules={[
									{
										required: true,
										message: t('forms.this_field_required'),
									},
								]}>
								<InputNumber placeholder={t('customers.weight')} style={{ width: '100%' }} />
							</Form.Item>
							<Form.Item
								label={t('customers.occupation')}
								name="occupation"
								rules={[
									{
										required: false,
										message: t('forms.this_field_required'),
									},
								]}>
								<Input placeholder={t('customers.occupation')} style={{ width: '100%' }} />
							</Form.Item>
							{!editCustomer || isVisitor ? (
								<>
									<Row>
										<hr />
										<Col span={24} style={{ fontWeight: 'bold' }}>
											{t('customers.package_details')}
										</Col>
										<hr />
									</Row>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										label={t('forms.service_type')}
										name="serviceId">
										{/* <Select loading={serviceLoading} placeholder="Service type">
											{services?.map((service, index) => (
												<Select.Option key={index} value={service.id}>
													{service?.serviceName}
												</Select.Option>
											))}
										</Select> */}
										<ServiceModal />
									</Form.Item>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										label={t('forms.membership')}
										name="membershipId">
										{/* <Select loading={membershipLoading} placeholder="Membership" disabled={!Boolean(serviceIdValue)}>
											{memberships?.map((membership, index) => (
												<Select.Option key={index} value={membership.id}>
													{membership?.membershipName} - {membership?.offerPrice || membership?.originalPrice}
												</Select.Option>
											))}
										</Select> */}
										<MembershipModal serviceId={serviceIdValue} />
									</Form.Item>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										label={t('forms.amount')}
										name="amount">
										<InputNumber placeholder={t('forms.amount')} style={{ width: '100%' }} />
									</Form.Item>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										label={t('forms.paid')}
										name="paid">
										<InputNumber placeholder={t('forms.paid')} style={{ width: '100%' }} />
									</Form.Item>
									<Form.Item initialValue={0} label={t('forms.balance')} name="balance">
										<InputNumber placeholder={t('forms.balance')} disabled style={{ width: '100%' }} />
									</Form.Item>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										initialValue={moment()}
										label={t('customers.joining_date')}
										name="joiningDate">
										<DatePicker style={{ width: '100%' }} format={DATE_FORMAT.DD_MM_YYYY} />
									</Form.Item>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										label={t('forms.paid_on')}
										name="paidOn">
										<DatePicker style={{ width: '100%' }} format={DATE_FORMAT.DD_MM_YYYY} />
									</Form.Item>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										label={t('forms.next_bill_date')}
										name="nextBillDate">
										<DatePicker style={{ width: '100%' }} format={DATE_FORMAT.DD_MM_YYYY} />
									</Form.Item>
									<Form.Item
										rules={[
											{
												required: true,
												message: t('forms.this_field_required'),
											},
										]}
										label={t('forms.payment_method')}
										name="paymentMethod">
										<Radio.Group style={{ width: '100%' }} options={PAYMENT_METHOD} optionType="button" buttonStyle="solid" />
									</Form.Item>
									{paymentMethodValue === PAYMENT_METHOD[1]?.value && (
										<Form.Item
											label={t('forms.upi_transaction_id')}
											name="upiTransactionId"
											rules={[
												{
													required: true,
													message: t('forms.this_field_required'),
												},
											]}>
											<InputNumber placeholder={t('forms.upi_transaction_id')} style={{ width: '100%' }} />
										</Form.Item>
									)}
									<Form.Item
										label={t('forms.remarks')}
										name="remarks"
										rules={[
											{
												required: false,
												message: t('forms.this_field_required'),
											},
										]}>
										<Input.TextArea />
									</Form.Item>
								</>
							) : null}
							<FileUpload
								action={'/'}
								afterUpload={() => {}}
								max={1}
								fileList={form.getFieldValue('image')}
								setFileList={setCustomerImage}
							/>
							<Form.Item
								wrapperCol={{
									offset: 0,
									span: 24,
								}}>
								<Row className="space-between" style={{ paddingTop: 20, width: '100%', margin: 0 }}>
									<Button danger style={{ width: '49%' }} onClick={() => setCustomerAddModal(false)}>
										{t('forms.cancel')}
									</Button>
									<Button loading={loading} style={{ width: '49%' }} type="primary" htmlType="submit">
										{editCustomer ? t('common.update') : t('common.save')}
									</Button>
								</Row>
							</Form.Item>
						</Form>
					</Col>
				</Row>
			</Drawer>
			<AddCoach {...{ coachAddModal, setCoachAddModal, refreshList: getCoaches, handleClose: () => setCoachAddModal(false) }} />
		</>
	);
};

export default AddCustomer;
